import { useLocation, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Phone } from 'lucide-react';
import { REAL_CATEGORIES, COMPANY } from '../data/site';
import { Reveal } from '../components/Motion';
import MediaGallery from '../components/MediaGallery';

// Single-project category detail: one image, title, short subtitle. Nothing else.
export default function RealisationCategory() {
  const location = useLocation();
  const slug = location.pathname.split('/').pop();
  const cat = REAL_CATEGORIES.find((c) => c.slug === slug);
  if (!cat || cat.hideDetail) return <Navigate to="/realisations" replace />;

  return (
    <section className="relative">
      {/* Content column */}
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-8 md:pt-16 pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: title + subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="overline mb-6">◍ {cat.title}</div>
            <h1 className="font-display italic text-[12vw] sm:text-[8vw] lg:text-[5.4vw] leading-[0.95] tracking-tight">
              {cat.detailTitle}
            </h1>
            <p className="mt-6 md:mt-8 max-w-md text-white/70 text-base md:text-lg leading-relaxed">
              {cat.detailSubtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="shine-wrap inline-flex items-center gap-3 bg-white text-black px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/90"
              >
                Demander un devis <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                className="shine-wrap inline-flex items-center gap-3 border border-white/25 px-6 h-12 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10"
              >
                <Phone size={14} strokeWidth={1.5} /> Appeler
              </a>
            </div>

            <Link
              to="/realisations"
              className="mt-10 inline-flex items-center gap-2 link-underline font-mono text-[10px] tracking-[0.22em] uppercase text-white/60 hover:text-white"
            >
              ← Autres catégories
            </Link>
          </motion.div>

          {/* Right: single image — compact and elegant */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
            className="col-span-12 lg:col-span-5 lg:sticky lg:top-32"
          >
            <div
              className="hero-media relative w-full max-w-[420px] lg:max-w-none mx-auto aspect-[4/3] md:aspect-[4/3] lg:aspect-[4/5] overflow-hidden rounded-2xl bg-black border border-white/10"
              style={{ boxShadow: 'var(--shadow-elev)' }}
            >
              <img
                src={cat.detailImage}
                alt={cat.detailTitle}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: 'grayscale(10%) contrast(1.04)' }}
              />
              {/* Extremely light gradient just enough for chrome legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              <div className="absolute left-5 top-5 md:left-7 md:top-7 font-mono text-[10px] tracking-[0.24em] uppercase text-white/85 glass px-3 py-1">
                {cat.title}
              </div>
              <div className="absolute right-5 bottom-5 md:right-7 md:bottom-7 font-mono text-[10px] tracking-[0.24em] uppercase text-white/85 glass px-3 py-1">
                Ponthieu · Paris
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Gallery — shown only when the category provides media */}
      {cat.gallery && cat.gallery.length > 0 && (
        <div className="relative border-t border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
            <Reveal>
              <div className="overline mb-4">◍ {cat.title}</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[52px] leading-[1.02] tracking-tight">
                Galerie {cat.title}
              </h2>
              <p className="mt-4 md:mt-5 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
                Découvrez une sélection de nos réalisations en photos et en vidéos.
              </p>
            </Reveal>

            <Reveal delay={0.1} className="mt-10 md:mt-14">
              <MediaGallery items={cat.gallery} />
            </Reveal>
          </div>
        </div>
      )}
    </section>
  );
}
